<template>
    <div class="container">
        <v-title :lineage="lineage">
            {{pageTitle}}
            <div slot="actions">
                <router-link
                    v-if="$auth.$pages[addRoute.name].isAllowed"
                    :to="addRoute"
                    class="btn btn-link">{{addLabel}}<v-icon class="margin-left-xs" icon="arrow-right"/></router-link>
            </div>
        </v-title>
        <div class="grid">
            <div class="grid-item">
                <div class="tile position-relative">
                    <v-inline-loader v-if="isLoading"/>
                    <r-tabs :divided="true" v-if="ruleSet && !hasError" content-class="no-padding" menu-class="no-margin-bottom">
                        <r-tab
                            :name="getTabLabel('rules')"
                            :to="getTabRoute('rules')">
                            <rule-set
                                show="rules"
                                :rule-set="ruleSet"
                                :event="systemEvent"
                                @input="updateRuleSet"/>
                        </r-tab>
                        <r-tab
                            :name="getTabLabel('binds')"
                            :to="getTabRoute('binds')">
                            <rule-set
                                show="binds"
                                :rule-set="ruleSet"
                                :event="systemEvent"
                                @input="updateRuleSet"/>
                        </r-tab>
                    </r-tabs>
                    <div
                        v-else-if="!ruleSet && hasError"
                        class="message message-negative stack-m">
                        <div class="message-content">
                            <p>There was an error while trying to access this event. Please try again later or contact support.</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import EventModel from '@/common/models/rules/event-model';
    import vTitle from '@/components/v-title';
    import {vIcon} from '@/components/form';
    import vInlineLoader from '@/components/v-inline-loader';
    import LoadStateMixin from '@/mixins/load-state-mixin';
    import _ from '@/common/helpers';
    import RuleSetModel from '@/common/models/rules/rule-set-model';
    import {extractCustomFieldResources} from '@/modules/rules-engine/components/condition-builder/get-fields-from-pathways';
    import RuleSet from './components/ruleset/rule-set';

    export default {
        name: 'event-rules',
        mixins: [LoadStateMixin],
        components: {
            vTitle, vIcon, RuleSet, vInlineLoader,
        },
        props: {
            eventType: String,
            eventId: String,
            view: {
                type: String,
                default: 'rules',
            },
        },
        computed: {
            addRoute() {
                if (this.view === 'rules') {
                    return {name: 'add-system-rule'};
                }
                return {name: 'add-system-bind'};
            },
            addLabel() {
                if (this.view === 'rules') {
                    return 'Add Rule';
                }
                return 'Add Bind';
            },
            systemEvent() {
                return new EventModel({eventType: this.eventType});
            },
            pageTitle() {
                return _.unKebabCase(this.eventType);
            },
            lineage() {
                return [
                    {
                        'System Events': {
                            name: 'system-events',
                        },
                    },
                ];
            },
        },
        data() {
            return {
                ruleSet: null,
                isLoading: false,
                hasError: false,
            };
        },
        methods: {
            /**
             * Return the correct API namespace based
             * on the type of event we're viewing.
             * @param callback
             */
            getDelegate(callback) {
                return this.$api(({events}) => callback(events));
            },
            async fetchData() {
                try {
                    const {eventType} = this;
                    await this.cacheCustomFields();
                    const {fields} = await this.getDelegate(type => type.getRules({eventType}));
                    this.setRuleSet(fields);
                    await this.$store.dispatch('rulesEngine/parseLabels', fields.rules);
                } catch (err) {
                    this.hasError = true;
                }
            },
            async updateRuleSet({ruleSet, message}) {
                this.setRuleSet(ruleSet);
                await this.pushUpdate(message);
            },
            setRuleSet(ruleSet) {
                this.ruleSet = new RuleSetModel(ruleSet);
            },
            async pushUpdate(message) {
                this.isLoading = true;
                try {
                    const typeFilter = {eventType: this.eventType};
                    const filters = {
                        limit: 1,
                        ...typeFilter,
                    };
                    const {items} = await this.getDelegate(type => type.getRulesHistory(filters));
                    const [latest] = items;
                    // check if the latest event is of the same version
                    if (latest.fields.version !== this.ruleSet.version) {
                        this.$toast.negative(
                            'This event has been modified since you viewed it. Your modification was discarded and the data was refreshed.',
                            {delay: 10000},
                        );
                        await this.fetchData();
                        return;
                    }
                    const params = {
                        data: this.ruleSet.getPayload(),
                        ...typeFilter,
                    };
                    const {fields} = await this.getDelegate(type => type.updateRules(params));
                    // update local version
                    this.ruleSet.version = fields.version;
                    if (message) {
                        this.$toast.positive(message);
                    }
                } catch (error) {
                    this.$toast.fromError(error, `Unable to modify ${_.unKebabCase(this.eventType)} at this time.`);
                } finally {
                    this.isLoading = false;
                }
            },
            getTabLabel(type) {
                let particle = '';
                if (this.ruleSet[`${type}Count`]) {
                    const count = this.ruleSet[`${type}Count`];
                    particle = ` (${count})`;
                }
                return `${_.capitalize(type)}${particle}`;
            },
            async cacheCustomFields() {
                const fetches = extractCustomFieldResources(this.systemEvent.pathways)
                    .map(resource => this.$store.dispatch('rulesEngine/fetchCustomFields', resource));
                await Promise.all(fetches);
            },
            getTabRoute(target) {
                return {
                    name: this.$route.name,
                    params: {
                        eventType: this.eventType,
                        view: target,
                    },
                };
            },
        },
    };
</script>

<style scoped>

</style>
